import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import CommonPageHeader from "../../common/ui/page-header/CommonPageHeader";
import FetchingSpinner from "../../common/FetchingSpinner";
import { WebStorage, WebStorageNames } from "../../../utils";
import { getMyTickets, searchCompanyTickets, TICKET_STATUS } from "./ticketApi";
import { formatTicketDate, PriorityBadge, STATUS_OPTIONS, StatusBadge } from "./helpSupportShared";
import "../documents/Documents.css";
import "./HelpSupport.css";

const getRoleId = () => {
  const r = WebStorage.getLocalStore(WebStorageNames.RoleId) ?? localStorage.getItem("roleId");
  return Number(r);
};

const HelpSupportPage = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const isAdmin = getRoleId() === 1 || getRoleId() === 2;

  const [scope, setScope] = useState("mine");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [tickets, setTickets] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [query, setQuery] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      if (isAdmin && scope === "company") {
        const params = {
          page: 1,
          take: 50,
          sortBy: "createdDate",
          sortDirection: "desc",
        };
        if (query.trim()) params.query = query.trim();
        if (statusFilter !== "") params.status = Number(statusFilter);
        const result = await searchCompanyTickets(params);
        setTickets(Array.isArray(result.items) ? result.items : []);
      } else {
        const rows = await getMyTickets(true);
        let list = Array.isArray(rows) ? rows : [];
        if (statusFilter !== "") {
          list = list.filter((x) => Number(x.status ?? x.Status) === Number(statusFilter));
        }
        if (query.trim()) {
          const q = query.trim().toLowerCase();
          list = list.filter((x) => {
            const title = (x.title ?? x.Title ?? "").toLowerCase();
            const type = (x.problemType ?? x.ProblemType ?? "").toLowerCase();
            return title.includes(q) || type.includes(q);
          });
        }
        setTickets(list);
      }
    } catch (err) {
      setError(err?.message || t("app.helpSupport.loadFailed", { defaultValue: "Failed to load tickets." }));
      setTickets([]);
    } finally {
      setLoading(false);
    }
  }, [isAdmin, scope, statusFilter, query, t]);

  useEffect(() => {
    load();
  }, [load]);

  const subtitle = useMemo(
    () =>
      t("app.helpSupport.listSubtitle", {
        defaultValue: "Track the tickets you raised and see progress updates.",
      }),
    [t]
  );

  return (
    <div className="help-support-page documents-page">
      <CommonPageHeader
        icon={<i className="bi bi-life-preserver" />}
        title={t("app.header.helpAndSupport")}
        description={subtitle}
        rightContent={
          <div className="help-support-actions">
            <button
              type="button"
              className="help-support-primary-btn"
              onClick={() => history.push("/help-support/new")}
            >
              {t("app.helpSupport.newTicket", { defaultValue: "New ticket" })}
            </button>
          </div>
        }
      />

      {isAdmin ? (
        <div className="help-support-tabs" role="tablist">
          <button
            type="button"
            className={`help-support-tab ${scope === "mine" ? "is-active" : ""}`}
            onClick={() => setScope("mine")}
          >
            {t("app.helpSupport.myTickets", { defaultValue: "My tickets" })}
          </button>
          <button
            type="button"
            className={`help-support-tab ${scope === "company" ? "is-active" : ""}`}
            onClick={() => setScope("company")}
          >
            {t("app.helpSupport.companyTickets", { defaultValue: "Company tickets" })}
          </button>
        </div>
      ) : null}

      <div className="help-support-filters">
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t("app.helpSupport.searchPlaceholder", { defaultValue: "Search by title or type…" })}
        />
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="">{t("app.helpSupport.allStatuses", { defaultValue: "All statuses" })}</option>
          {STATUS_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      {error ? <div className="help-support-error">{error}</div> : null}

      {loading ? (
        <FetchingSpinner />
      ) : tickets.length === 0 ? (
        <div className="help-support-empty">
          <p>
            {t("app.helpSupport.empty", {
              defaultValue: "No tickets yet. Create one and we’ll help you track it here.",
            })}
          </p>
          <button
            type="button"
            className="help-support-primary-btn"
            onClick={() => history.push("/help-support/new")}
          >
            {t("app.helpSupport.newTicket", { defaultValue: "New ticket" })}
          </button>
        </div>
      ) : (
        <div className="help-support-list">
          {tickets.map((ticket) => {
            const id = ticket.id ?? ticket.Id;
            const title = ticket.title ?? ticket.Title ?? "—";
            const problemType = ticket.problemType ?? ticket.ProblemType ?? "—";
            const status = ticket.status ?? ticket.Status ?? TICKET_STATUS.Open;
            const statusName = ticket.statusName ?? ticket.StatusName;
            const priority = ticket.priority ?? ticket.Priority;
            const priorityName = ticket.priorityName ?? ticket.PriorityName;
            const created = ticket.createdDate ?? ticket.CreatedDate;
            const updated = ticket.updatedDate ?? ticket.UpdatedDate;
            const createdByName = ticket.createdByName ?? ticket.CreatedByName;

            return (
              <button
                key={id}
                type="button"
                className="help-support-ticket-row"
                onClick={() => history.push(`/help-support/${encodeURIComponent(id)}`)}
              >
                <div>
                  <h3 className="help-support-ticket-title">{title}</h3>
                  <div className="help-support-ticket-meta">
                    <span>{problemType}</span>
                    {scope === "company" && createdByName ? <span>{createdByName}</span> : null}
                    <span>
                      {t("app.helpSupport.updated", { defaultValue: "Updated" })}: {formatTicketDate(updated || created)}
                    </span>
                  </div>
                </div>
                <div className="help-support-badges">
                  <StatusBadge status={status} statusName={statusName} />
                  <PriorityBadge priority={priority} priorityName={priorityName} />
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default HelpSupportPage;
